/**
 * Canonical roster of LegalFlare operators — the assignee universe for
 * Dashboard to-dos. Separate from `OPERATOR_ALLOWLIST` (the sign-in
 * gate in `apps/worker/src/auth/allowlist.ts`): an operator can be
 * addressable as an assignee even when their sign-in is temporarily
 * disabled. To-dos addressed to a roster-but-not-allowlisted operator
 * sit waiting until the allowlist re-opens.
 *
 * Slice E reads this for the Create modal's assignee dropdown.
 */
export interface Operator {
  email: string;
  displayName: string;
}

export const OPERATOR_ROSTER: readonly Operator[] = [
  { email: "chris.rose@legalflare.com", displayName: "Chris" },
] as const;

/**
 * Every operator other than `selfEmail`, roster order preserved. The
 * Dashboard's viewer control reads this to render one segment per peer.
 *
 * An empty or whitespace-only `selfEmail` returns `[]`, not the whole
 * roster. That case is real, not hypothetical: the Dashboard calls this on
 * every render, and before `useMe` resolves it passes `""`. Filtering
 * against an empty string would match nobody and hand back everyone —
 * rendering the signed-in operator as their own peer for the width of the
 * auth-loading window. The caller happens to guard that today; making the
 * contract self-enforcing means the next caller does not have to know.
 */
export function peersOf(selfEmail: string): readonly Operator[] {
  const lower = selfEmail.trim().toLowerCase();
  if (lower === "") return [];
  return OPERATOR_ROSTER.filter((op) => op.email.toLowerCase() !== lower);
}

/**
 * The owner of LegalFlare — the one person the task seat guard does not hold
 * (#959). Chris, 2026-09-23: "Chris must be able to act on any task in his own
 * system. The seat guard can stay for when someone joins." So the guard on
 * PATCH /api/operations/tasks/:id refuses an actor who is neither the required
 * seat nor this address; everyone else, a new joiner included, stays
 * seat-gated.
 *
 * Deliberately not derived from OPERATOR_ALLOWLIST or OPERATOR_ROSTER: those
 * answer who may sign in and who can be assigned work, and a joiner added to
 * them must not inherit the owner's reach. The worker's route tests hold this
 * address to the allowlist, the roster and the `chris` ops identity so the
 * hand-typed copies cannot drift apart.
 */
export const SYSTEM_OWNER_EMAIL = "chris.rose@legalflare.com";

/** Case-insensitive, whitespace-tolerant check against SYSTEM_OWNER_EMAIL. */
export function isSystemOwner(email: string | null | undefined): boolean {
  if (typeof email !== "string") return false;
  return email.trim().toLowerCase() === SYSTEM_OWNER_EMAIL;
}

/** Case-insensitive lookup; returns the canonical entry or null. */
export function operatorByEmail(email: string): Operator | null {
  const lower = email.trim().toLowerCase();
  for (const op of OPERATOR_ROSTER) {
    if (op.email.toLowerCase() === lower) return op;
  }
  return null;
}

/**
 * True when a task seat holds an address that is not on OPERATOR_ROSTER (#1024):
 * someone who left, an address that was only ever planned, or a synthetic
 * identity such as ops-workstation@ or pm-2.0@ — none of which can open the
 * Dashboard and act on the task. An empty or absent seat (a watch's assignee)
 * is not a seat, so it is never outside the roster.
 *
 * The owner's Dashboard lists the live tasks with such a seat so they stay in
 * someone's view (GET /api/dashboard/outside-roster).
 */
export function isOutsideRoster(email: string | null | undefined): boolean {
  if (typeof email !== "string" || email.trim() === "") return false;
  return operatorByEmail(email) === null;
}
